<script setup lang="ts">
import { onMounted, onBeforeUnmount, ref, watch } from 'vue';

const props = defineProps<{
    open: boolean;
    title?: string;
    /**
     * Override the dialog's width utility classes. Defaults to a
     * comfortable form-modal width; pass e.g.
     * `w-[min(96vw,1100px)] max-w-none` for wide pickers.
     */
    widthClass?: string;
}>();
const emit = defineEmits<{ (e: 'close'): void }>();

const dialog = ref<HTMLDialogElement | null>(null);

watch(() => props.open, (v) => {
    if (!dialog.value) return;
    if (v) dialog.value.showModal();
    else if (dialog.value.open) dialog.value.close();
});

function onBackdropClick(ev: MouseEvent) {
    // Same trick the legacy app uses — clicks on the dialog element
    // itself (the backdrop) close it; clicks on .modal-card stop
    // propagation so they don't.
    if (ev.target === dialog.value) emit('close');
}

function onCancel(ev: Event) {
    ev.preventDefault();   // intercept Esc → fire our close instead
    emit('close');
}

onMounted(() => {
    if (props.open && dialog.value && !dialog.value.open) dialog.value.showModal();
});
onBeforeUnmount(() => {
    if (dialog.value && dialog.value.open) dialog.value.close();
});
</script>

<template>
    <dialog ref="dialog" @click="onBackdropClick" @cancel="onCancel"
            class="m-auto rounded-2xl p-0 backdrop:bg-black/45"
            :class="widthClass || 'max-w-xl w-[min(92vw,560px)]'">
        <div class="bg-frvb-card rounded-2xl">
            <header v-if="title || $slots.header"
                    class="px-5 py-4 border-b border-frvb-border flex items-center justify-between">
                <h3 class="font-extrabold text-base">
                    <slot name="header">{{ title }}</slot>
                </h3>
                <button @click="emit('close')"
                        class="text-frvb-muted hover:text-frvb-ink text-xl leading-none px-2"
                        aria-label="Close">×</button>
            </header>
            <div class="p-5">
                <slot />
            </div>
            <footer v-if="$slots.footer"
                    class="px-5 py-3 border-t border-frvb-border flex items-center justify-end gap-2">
                <slot name="footer" />
            </footer>
        </div>
    </dialog>
</template>
